import { useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import useEventListener from 'ahooks/lib/useEventListener';
import MobxWrap from '@/components/MobxWrap';
import { isReachTop } from '@/utils';
import styles from './index.module.less';
import ComponentsErrorBoundary from '../ErrorBoundary/ComponentsErrorBoundary';
import cls from 'classnames';
import { useSearchPage, checkIsSearchResultsPage } from '@/pages/search/utils';

const TopBar = dynamic(import('./TopBar'));
const Nav = dynamic(import('./Nav'));
const NavMobile = dynamic(import('./Nav/index.mobile'));
const HeaderEntry = dynamic(import('../activity/HeaderEntry'), { ssr: false });
const IntroMenu = dynamic(import('@/pages/reseller/components/IntroMenu'), { ssr: false });

const Header = MobxWrap(['area', 'auth', 'meta', 'app'], (props) => {
  const { meta, app } = props;
  const { setStateValue, navStateInfos } = meta;
  const { G_CurrentPathName, isMobile, isOrderConfirmPage } = app;
  const [showMobileNav, setShowMobileNav] = useState(false);
  const scrollRef = useRef({ prevTop: 0 });
  const eleRef = useRef(null);
  // 导航的展示逻辑 避免阻碍SSR，不放在useEffect中处理
  const notShowNav = isOrderConfirmPage;
  const notShowTopBar = isOrderConfirmPage;
  const { current: showHeader } = useRef({ topBar: !notShowTopBar, nav: !notShowNav }); // topbar,nav:NavBar内的导航内容显示
  const handleBindScrollEventFunc = () => {
    if (isReachTop()) {
      if (!navStateInfos.isNavFixed) {
        navStateInfos.isNavFixed = true;
        setStateValue('navStateInfos', navStateInfos);
      }
    } else {
      if (navStateInfos.isNavFixed) {
        navStateInfos.isNavFixed = false;
        setStateValue('navStateInfos', navStateInfos);
      }
    }
  };

  // 确定页面是否触顶
  useEventListener('scroll', handleBindScrollEventFunc);

  const setIsNavFixIsHomePage = () => {
    if (G_CurrentPathName === '/') {
      navStateInfos.isHomePage = true;
      setStateValue('navStateInfos', navStateInfos);
    }
    handleBindScrollEventFunc();
  };

  // 处理TopBar消失出现的逻辑
  const handleListenScroll = () => {
    if (document.body.getAttribute('data-HideCustomerService') === 'true') return;
    // 对于弹窗导致body的滚动条消失的情况,需要特殊处理;window.scrollY为0时,取Math.abs(document.body.style.top.replace('px', ''))进行比较
    // Math.abs(document.body.style.top.replace('px', '')) === window.scrollY为0前最后一次的滚动高度
    // scrollRef.current.prevTop === window.scrollY上一次的滚动高度
    const isMoveUp =
      (window.scrollY || Math.abs(document.body.style.top.replace('px', ''))) < scrollRef.current.prevTop;
    const visible = (window.scrollY || Math.abs(document.body.style.top.replace('px', ''))) > 120 ? isMoveUp : true;

    const eleContent = eleRef.current ?? document.querySelector(`#g-header`);
    eleRef.current = eleContent;
    scrollRef.current.prevTop = window.scrollY;

    // 控制展示还是隐藏
    if (!visible) {
      app.setGlobalValue('G_TopBarVisible', false);
      eleContent.classList.add(styles.hide);
      eleContent.classList.add('g-header-hide');
    } else {
      app.setGlobalValue('G_TopBarVisible', true);
      eleContent.classList.remove(styles.hide);
      eleContent.classList.remove('g-header-hide');
    }
  };
  // 是否为搜索页面
  const { isSearchResultsPage } = useSearchPage();

  // 处理TopBar逻辑
  useEffect(() => {
    // 如果是搜索结果页，则不需要topbar的sticky效果 @卢彩凤
    if (checkIsSearchResultsPage()) return;

    if (showHeader.topBar) {
      window.addEventListener('scroll', handleListenScroll);
    }
    return () => {
      window.removeEventListener('scroll', handleListenScroll);
    };
  }, []);

  // 处理Nav逻辑
  useEffect(() => {
    setIsNavFixIsHomePage(); // 确定当前页是否为首页以及页面是否触顶的信息
    const handleResize = () => {
      if (isMobile) return;
      setShowMobileNav(window.innerWidth <= 1200);
    };

    if (showHeader.nav) {
      meta.fetchNavigationBar(); // 处理导航数据信息
      window.addEventListener('resize', handleResize);
      handleResize();
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div
      id='g-header'
      className={cls({
        [styles.gHeader]: true,
        [styles.noSticky]: isSearchResultsPage,
      })}
    >
      <div id='g-activity-entry' className={styles.activityEntryWrap}>
        <ComponentsErrorBoundary name='G_Header HeaderEntry'>
          {app.isMobile ? <HeaderEntry /> : null}
        </ComponentsErrorBoundary>
      </div>
      <div className={styles.mainWrap}>
        <ComponentsErrorBoundary name='G_Header TopBar'>
          {showHeader.topBar && <TopBar showMobileNav={showMobileNav} />}
        </ComponentsErrorBoundary>

        <ComponentsErrorBoundary name='G_Header Nav'>
          {app.isMobile || showMobileNav ? (
            <NavMobile showMobileNav={showMobileNav} showNavContent={showHeader.nav} />
          ) : (
            <Nav showNavContent={showHeader.nav} />
          )}
        </ComponentsErrorBoundary>
      </div>
      <div className={styles.resellerIntroWrap}>{app.G_IsResellerIntro && <IntroMenu />}</div>
    </div>
  );
});

export default Header;
